import { Link, useOutletContext, useParams } from "react-router-dom"
import { useComputed, useSignal } from "@preact/signals-react"
import { useEffect } from "react"

import Breadcrumb from 'react-bootstrap/Breadcrumb'
import Dropdown from 'react-bootstrap/Dropdown'
import { InputGroup } from "react-bootstrap"
import Nav from 'react-bootstrap/Nav'
import Pagination from 'react-bootstrap/Pagination'
import Table from 'react-bootstrap/Table'

import AdministrationTabs from '../components/AdministrationTabs.jsx'
import CopyRowAsJSON from '../../../components/CopyRowAsJSON.jsx'
import Icon from "../../../components/Icon.jsx"
import LoadingAnimation from "../../../components/LoadingAnimation.jsx"
import PageSizeSelector from "../../../components/PageSizeSelector.jsx"
import PageTitle from "../../../components/PageTitle.jsx"
import TableColumnHeader from '../../../components/TableColumnHeader.jsx'
import TimestampWithUTCTooltip from '../../../components/TimestampWithUTCTooltip.jsx'

import { notifyException } from "../../../state/actions/notificationActions.js"
import { fetchEphemeralUserList, fetchSystemUserList } from "../../../state/actions/userActions.js"
import { updatePageSize } from '../../../state/actions/userSettingsActions.js'

import { businessEntityDictionarySignal } from "../../../state/signals/businessEntitySignals.js"
import { systemUserListSignal, systemUserListLoadedSignal, ephemeralUserListSignal, ephemeralUserListLoadedSignal } from "../../../state/signals/userSignals.js"
import { pageSizeSignal } from '../../../state/signals/userSettingsSignals.js'

import { SystemUserType, SortDirectionType, LifespanType } from "../../../utils/constants.js"
import { computeDisplayData, computeSortedData, computeStartDisplayRowNumber, computeEndDisplayRowNumber, computeTotalPages } from '../../../utils/table.js'
import { getUserStatusText } from "../../../utils/utils.js"

const SystemUsersPage = function () {

	const { beId, systemUserType } = useParams()
	const { config, accessToken, userPermissionsSummary, accordionData: sidebarAccordionData } = useOutletContext()

	const canViewSystemUsersComputed = useComputed(
		() => {
			return userPermissionsSummary?.value?.canViewSystemUsers
		}
	)

	useEffect(() => {
		switch (systemUserType) {
			case SystemUserType.Service:
				fetchSystemUserList(beId, config, accessToken)
				break
			case SystemUserType.Ephemeral:
				fetchEphemeralUserList(beId, config, accessToken)
				break
			default:
				throw Error(`Invalid system user type "${systemUserType}"`)
		}
	}, [systemUserType, beId, config, accessToken])

	const userListComputed = useComputed(
		() => {
			switch (systemUserType) {
				case "serviceUsers":
					return systemUserListSignal.value
				case "ephemeralUsers":
					return ephemeralUserListSignal.value
				default:
					throw Error(`Invalid system user type "${systemUserType}"`)
			}
		}
	)

	const userListLoadedComputed = useComputed(
		() => {
			switch (systemUserType) {
				case "serviceUsers":
					return systemUserListLoadedSignal.value
				case "ephemeralUsers":
					return ephemeralUserListLoadedSignal.value
				default:
					throw Error(`Invalid system user type "${systemUserType}"`)
			}
		}
	)

	const currentPageSignal = useSignal(1)
	const sortColumnSignal = useSignal('')
	const sortDirectionSignal = useSignal(SortDirectionType.DESCENDING)

	const sortedDataComputed = useComputed(computeSortedData(userListComputed, sortColumnSignal, sortDirectionSignal))
	const totalPagesComputed = useComputed(computeTotalPages(userListComputed))
	const displayDataComputed = useComputed(computeDisplayData(currentPageSignal, sortedDataComputed))
	const startDisplayRowNumberComputed = useComputed(computeStartDisplayRowNumber(currentPageSignal))
	const endDisplayRowNumberComputed = useComputed(computeEndDisplayRowNumber(currentPageSignal, userListComputed))

	const handlePageSizeSelect = (v) => {
		currentPageSignal.value = 1
		updatePageSize(parseInt(v, 10))
	}

	const getLifespanTypeName = (type) => {
		switch (type) {
			case LifespanType.EndDateTime:
				return "End Date"
			case LifespanType.SingleUse:
				return "Single Use"
			case LifespanType.Window:
				return "Window"
			default:
				return "Unknown"
		}
	}

	const getBusinessEntityName = (businessEntityId) => {
		let name = null
		if (businessEntityId in businessEntityDictionarySignal.value) {
			const businessEntity = businessEntityDictionarySignal.value[businessEntityId]
			if (businessEntity.name) {
				name = businessEntity.name
			}
		}
		if (name == null) {
			return `Uknown business entity ${businessEntityId}`
		}
		return name
	}

	function TableRows({ items }) {
		try {
			if (!userListLoadedComputed.value) {
				return <tr><td colSpan={6}><LoadingAnimation /></td></tr>
			}
			if (items && items.length) {
				let keys = new Set()
				let rows = []
				for (let i = 0; i < items.length; i++) {
					const item = items[i]
					let key = item.id
					if (keys.has(key)) {
						continue
					}
					keys.add(key)
					if (systemUserType == SystemUserType.Service) {
						rows.push(
							<tr key={key}>
								<td>
									<Dropdown>
										<Dropdown.Toggle variant="link" size="sm" id="actions">
											<Icon icon="gear" width="16" height="16"></Icon>
										</Dropdown.Toggle>
										<Dropdown.Menu>
											<Dropdown.Item as={Link} to={`../systemUsers/${systemUserType}/${item.id}`}>
												<Icon icon="pencil"></Icon>&nbsp;Edit Profile
											</Dropdown.Item>
											<CopyRowAsJSON item={item} field="username" />
										</Dropdown.Menu>
									</Dropdown>
								</td>
								<td>{item.username}</td>
								<td>{item.firstName}</td>
								<td>{item.lastName}</td>
								<td>{item.email}</td>
								<td>{getUserStatusText(item)}</td>
							</tr>
						)
					}
					if (systemUserType == SystemUserType.Ephemeral) {
						rows.push(
							<tr key={key}>
								<td>
									<Dropdown>
										<Dropdown.Toggle variant="link" size="sm" id="actions">
											<Icon icon="gear" width="16" height="16"></Icon>
										</Dropdown.Toggle>
										<Dropdown.Menu>
											<Dropdown.Item as={Link} to={`../systemUsers/${systemUserType}/${item.id}`}>
												<Icon icon="pencil"></Icon>&nbsp;Edit Profile
											</Dropdown.Item>
											<Dropdown.Item as={Link} to={`../systemUsers/${systemUserType}/${item.id}`}>
												<Icon icon="gear"></Icon>&nbsp;Deactivate
											</Dropdown.Item>
											<CopyRowAsJSON item={item} field="username" />
										</Dropdown.Menu>
									</Dropdown>
								</td>
								<td>{item.createdBy}</td>
								<td>{getBusinessEntityName(item.businessEntityId)}</td>
								<td>{getLifespanTypeName(item.lifespan?.lifeSpanType)}</td>
								<td><TimestampWithUTCTooltip>{item.lastUpdatedAt}</TimestampWithUTCTooltip></td>
								<td>{item.allowDuplicateSessions === true ? "True" : "False"}</td>
							</tr>
						)
					}
				}
				return rows
			}
			return (
				<tr>
					<td colSpan={6} className="text-center">This business entity does not have any system users</td>
				</tr>
			)
		} catch (x) {
			notifyException(x, "Error generating table rows")
			return null
		}
	}

	const setCurrentPage = function (pageNum) {
		if (currentPageSignal.value != pageNum) {
			currentPageSignal.value = pageNum
		}
	}

	function PaginationItems() {
		let items = []
		for (let i = 1; i <= totalPagesComputed.value; i++) {
			items.push(
				<Pagination.Item key={i} active={i === currentPageSignal.value} onClick={() => { setCurrentPage(i) }}>{i}</Pagination.Item>
			)
		}
		return items
	}

	return (
		<>
			<Breadcrumb className="px-3 pt-1 bg-light">
				<Breadcrumb.Item linkAs={Link} linkProps={{ to: `/be/${beId}/management/accounts` }}>Administration</Breadcrumb.Item>
				<Breadcrumb.Item active>System Users</Breadcrumb.Item>
			</Breadcrumb>
			<PageTitle title='System Users' canEditSignal={canViewSystemUsersComputed.value} />
			<AdministrationTabs activeKey="5"></AdministrationTabs>
			<div>
				<Nav className="justify-content-between px-3 py-1 my-2 bg-light">
					<Nav.Item className="me-auto">
						<PageSizeSelector pageSizeSignal={pageSizeSignal} handlePageSizeSelect={handlePageSizeSelect} />
					</Nav.Item>
					<Nav.Item>
						<InputGroup id="systemUserType">
							<Link to={`../systemUsers/${SystemUserType.Service}`} className="btn btn-primary" data-selected={systemUserType === SystemUserType.Service ? "selected" : undefined} onClick={
								(event) => {
									if (systemUserType === SystemUserType.Service) {
										event.preventDefault()
									}
								}
							}>Service Accounts</Link>
							{
								(userPermissionsSummary && userPermissionsSummary.value && userPermissionsSummary.value.canViewEphemeralUsers) ? (
									<Link to={`../systemUsers/${SystemUserType.Ephemeral}`} className="btn btn-primary" data-selected={systemUserType === SystemUserType.Ephemeral ? "selected" : undefined} onClick={
										(event) => {
											if (systemUserType === SystemUserType.Ephemeral) {
												event.preventDefault()
											}
										}
									}>Ephemeral Users</Link>
								) : (<></>)
							}
						</InputGroup>
					</Nav.Item>
				</Nav>
				<Table responsive striped hover size="sm" className="cc-table">
					{(systemUserType == SystemUserType.Service) && (
						<thead>
							<tr>
								<td></td>
								<TableColumnHeader title={'Username'} sortable={true} datafield={'username'} sortColumnSignal={sortColumnSignal} sortDirectionSignal={sortDirectionSignal}></TableColumnHeader>
								<TableColumnHeader title={'First Name'} sortable={true} datafield={'firstName'} sortColumnSignal={sortColumnSignal} sortDirectionSignal={sortDirectionSignal}></TableColumnHeader>
								<TableColumnHeader title={'Last Name'} sortable={true} datafield={'lastName'} sortColumnSignal={sortColumnSignal} sortDirectionSignal={sortDirectionSignal}></TableColumnHeader>
								<TableColumnHeader title={'Email Address'} sortable={true} datafield={'email'} sortColumnSignal={sortColumnSignal} sortDirectionSignal={sortDirectionSignal}></TableColumnHeader>
								<TableColumnHeader title={'Status'} sortable={false}>Status</TableColumnHeader>
							</tr>
						</thead>
					)}
					{(systemUserType == SystemUserType.Ephemeral) && (
						<thead>
							<tr>
								<td></td>
								<TableColumnHeader title={'Created By'} sortable={true} datafield={'createdBy'} sortColumnSignal={sortColumnSignal} sortDirectionSignal={sortDirectionSignal}></TableColumnHeader>
								<TableColumnHeader title={'Parent Organization'} sortable={true} datafield={'businessEntityId'} sortColumnSignal={sortColumnSignal} sortDirectionSignal={sortDirectionSignal}></TableColumnHeader>
								<TableColumnHeader title={'Lifespan Type'} sortable={false} datafield={'lifeSpanType'} sortColumnSignal={sortColumnSignal} sortDirectionSignal={sortDirectionSignal}></TableColumnHeader>
								<TableColumnHeader title={'Last Updated At'} sortable={true} datafield={'lastUpdatedAt'} sortColumnSignal={sortColumnSignal} sortDirectionSignal={sortDirectionSignal}></TableColumnHeader>
								<TableColumnHeader title={'Allow Duplicate Sessions'} datafield={'allowDuplicateSessions'} sortable={false}></TableColumnHeader>
							</tr>
						</thead>
					)}
					<tbody><TableRows items={displayDataComputed.value} /></tbody>
				</Table>
				{
					displayDataComputed.value.length > 0 && (
						<div>
							Displaying {startDisplayRowNumberComputed.value}-{endDisplayRowNumberComputed.value} of {userListComputed.value.length}
						</div>
					)
				}
				<Pagination className="px-3 d-flex flex-wrap" size="sm"><PaginationItems /></Pagination>
			</div>
		</>
	)
}
export default SystemUsersPage